import React, { useState, useEffect } from 'react';

import { Column, Row } from 'components/Layout/Grid';
import Text from 'components/Text';
import Select from 'components/Select';
import SwitchComponent from 'components/SwitchComponent';

import { CategoriesReasonsDataProps, ICategoriesReasons, IPipefyOption } from 'components/Select/interface';
import { ProtocolCategorySimpleProps, CategoryReasonFilterProps } from './interface';
import {
  categoriesToDisplayInConsumerManualProtocols,
  EnumCategories,
  reasonsToHideProtocolsConsumerManual
} from 'models/Ticket';

import TicketServices from 'services/ticket.service';

export const ProtocolCategorySimple: React.FC<ProtocolCategorySimpleProps> = (props) => {
  const {
    protocolCategory,
    setProtocolCategory,
    ticketReason,
    setTicketReason,
    protocolClassification,
    setProtocolClassification,
    isEditing
  } = props;

  const _protocolService = new TicketServices();

  const [allCategories, setAllCategories] = useState<Array<IPipefyOption>>([]);
  const [allReasons, setAllReasons] = useState<Array<IPipefyOption>>([]);
  const [allReasonsCompleted, setAllReasonsCompleted] = useState<Array<IPipefyOption>>([]);
  const [allClassifications, setAllClassifications] = useState<Array<IPipefyOption>>([]);

  useEffect(() => {
    if (!isEditing) {
      setProtocolClassification("");
      setTicketReason("");
    }

    if (protocolCategory === EnumCategories.LOGISTICA && !isEditing) {
      setAllReasons(allReasonsCompleted.filter(x =>
        x.label !== 'PREVISÃO DE ENTREGA' && x.label !== 'ATRASO DE ENTREGA'));
    } else {
      setAllReasons(allReasonsCompleted);
    }
  }, [protocolCategory]);

  useEffect(() => {
    getCategoriesReasons();
  }, []);

  async function getCategoriesReasons() {
    const options: ICategoriesReasons = await _protocolService.getCategoriesReasons();
    const { data } = options.data;

    await filterCategories(data);
    await filterReasons(data);
    await sortProtocolArray(data.categories);

    setAllCategories(data.categories);
    setAllReasons(data.reasons);
    setAllReasonsCompleted(data.reasons);
    setAllClassifications(data.classifications);
  }

  const filterCategories = async (data: CategoriesReasonsDataProps) => {
    data.categories = data.categories.filter((category: CategoryReasonFilterProps) =>
      categoriesToDisplayInConsumerManualProtocols.includes(category.label.toUpperCase()));
  };

  const filterReasons = async (data: CategoriesReasonsDataProps) => {
    if (!isEditing) {
      data.reasons = data.reasons.filter((reasons: CategoryReasonFilterProps) =>
        !reasonsToHideProtocolsConsumerManual.includes(reasons.label.toUpperCase()));
    }
  };

  const sortProtocolArray = async (array: Array<IPipefyOption>) => {
    array.sort((a, b) => {
      if (a.label > b.label) return 1;
      if (a.label < b.label) return -1;

      return 0;
    });
  };

  const disableFieldInEdit = () => {
    if (isEditing) {
      const categories: Array<string> = [
        EnumCategories.DEVOLUCAO,
        EnumCategories.RECLAMACAO_TECNICA
      ];

      return categories.includes(protocolCategory);
    }

    return false;
  };

  function disableProtocolClassification() {
    if (protocolCategory === EnumCategories.LOGISTICA)
      return ticketReason !== '4';

    const foundCategory = checkCategoriesRemainedEnabledInClassification();

    if (foundCategory && ticketReason !== '')
      return false;

    return true;
  }

  const checkCategoriesRemainedEnabledInClassification = () => {
    const categoriesRemainEnabled: Array<string> = [
      EnumCategories.DEVOLUCAO,
      EnumCategories.LOGISTICA,
      EnumCategories.RECLAMACAO_TECNICA,
      EnumCategories.COMERCIAL
    ];

    return categoriesRemainEnabled.includes(protocolCategory);
  };

  const subjectionClassification = () => {
    if (protocolCategory === EnumCategories.RECLAMACAO_TECNICA ||
      protocolCategory === EnumCategories.LOGISTICA)
      return ticketReason;

    return "";
  };

  const valueClassification = disableProtocolClassification() ?
    '' : protocolClassification;

  return (
    <Row className="flex-md-column">
      <Column className="flex-1">
        <Text className="mb-2 color-blue-gray" size={12}>
          Categoria do Protocolo
        </Text>
        <Select
          stateName="protocolCategory"
          onChange={(e) => setProtocolCategory(e.target.value)}
          className="w-100"
          name="Categoria do protocolo"
          id="PROTOCOL_CATEGORY_SIMPLE"
          data-testid="PROTOCOL_CATEGORY_SIMPLE"
          value={protocolCategory}
          required
          options={allCategories}
          disabled={disableFieldInEdit()}
        />
      </Column>
      <Column className="flex-1 ml-4 ml-sm-0 mt-md-2">
        <Text className="mb-2 color-blue-gray" size={12}>
          Motivo do Protocolo
        </Text>
        <Select
          stateName="ticketReason"
          onChange={(e) => setTicketReason(e.target.value)}
          className="w-100"
          disabled={!protocolCategory}
          name="Motivo do protocolo"
          subjection={protocolCategory}
          id="PROTOCOL_REASON_SIMPLE"
          data-testid="PROTOCOL_REASON_SIMPLE"
          value={ticketReason}
          required
          options={allReasons}
        />
      </Column>
      <SwitchComponent roles={['administrator', 'member']} type={['employee']}>
        <Column className="flex-1 ml-4 ml-sm-0 mt-md-2">
          <Text className="mb-2 color-blue-gray" size={12}>
            Classificação do Protocolo
          </Text>
          <Select
            stateName="protocolClassification"
            onChange={(e) => setProtocolClassification(e.target.value)}
            className="w-100"
            name="Classificação do protocolo"
            id="PROTOCOL_CLASSIFICATION_SIMPLE"
            data-testid="PROTOCOL_CLASSIFICATION_SIMPLE"
            disabled={disableProtocolClassification()}
            value={valueClassification}
            subjection={subjectionClassification()}
            options={allClassifications}
            category={protocolCategory}
          />
        </Column>
      </SwitchComponent>
    </Row>
  );
};